// Every query and mutation of the conversation screens. Keys are written here only.
//
// A conversation lives in one of two places, and its screens are the same in
// both (`ConvPlace`):
// - a workspace's (topics, broadcasts, the workspace's direct messages):
//   REST under /api/w/<slug>, keys under ["ws", slug, "cv", ...], so a team
//   event of that workspace drops just its part;
// - the personal direct messages (outside every workspace): REST under
//   /api/dms, keys under ["me", "dms", ...] (Me's events).
//
// Promise:
// - The messages of a topic are one stretch, newest last, without gaps but
//   the one a jump to the head leaves (`gap`, filled as the person comes
//   down to it). Reading again reads what is loaded so far and what came
//   after it (an edit, a removal or a reaction anywhere in it shows).
// - A message being sent is not written into the cache: the screen draws the
//   pending posts (`usePendingPosts`) after the stored ones, and the stored
//   message takes the pending one's place when the server answers.
// - Other writes (edit, remove, reactions, read point, pins) show at once and
//   roll back on failure. What is created carries an Idempotency-Key.
import {
  type InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  type QueryClient,
  type QueryKey,
  queryOptions,
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type DmListView,
  type DmView,
  dmPeersPath,
  type EmojiView,
  type InboxItem,
  MESSAGE_PAGE_MAX,
  type MessagePage,
  type MessageView,
  type PersonRef,
  type PinKind,
  type PinnedDocument,
  type PostResult,
  type ReactionView,
  type SpaceOverview,
  TOPIC_PAGE_MAX,
  type TopicKind,
  type TopicPage,
  type TopicView,
  type UnreadSummary,
} from "@trameria/cloud-api/conversations-shared";
import type {
  MemberView,
  TeamUserView,
} from "@trameria/cloud-api/workspace-shared";
import {
  type Api,
  ApiError,
  type ColumnGap,
  idempotencyKeyFor,
  inboxRowsSettled,
  newOptimisticId,
  optimisticMutation,
  putGapPage,
  putJumpPage,
  useApi,
} from "@trameria/ui-core";

const enc = encodeURIComponent;

/** Where a conversation's REST and cache live. */
export interface ConvPlace {
  /** The REST prefix of topics and messages (`<base>/topics/:id/messages`, `<base>/messages/:id`). */
  base: string;
  /** The REST prefix of direct messages (`<dms>`, `<dms>/:userId`, `<dms>/:userId/messages`). */
  dms: string;
  /** The prefix of its query keys. */
  key: readonly string[];
  /** The workspace, or null for the personal direct messages. */
  slug: string | null;
}

export const workspaceConvPlace = (slug: string): ConvPlace => ({
  base: `/api/w/${enc(slug)}`,
  dms: `/api/w/${enc(slug)}/dms`,
  key: ["ws", slug, "cv"],
  slug,
});

export const PERSONAL_DMS: ConvPlace = {
  base: "/api/dms",
  dms: "/api/dms",
  key: ["me", "dms"],
  slug: null,
};

/** Every conversation query of a workspace (a refused stream cursor drops them). */
export const convPrefix = (slug: string) => ["ws", slug, "cv"] as const;

// ---- topics --------------------------------------------------------------------

export const MESSAGES_PAGE = 50;

/**
 * Which topics a list asks for: the kinds, one space and a typed word are the
 * server's to narrow (a topic not read yet is found); unread is the screen's.
 */
export interface TopicFilter {
  kinds?: readonly TopicKind[];
  spaceId?: string | null;
  q?: string;
}

export const topicsPrefix = (slug: string) =>
  ["ws", slug, "cv", "topics"] as const;

/** The topics of the spaces one sees, newest activity first. */
export const topicsQuery = (api: Api, slug: string, filter: TopicFilter = {}) =>
  infiniteQueryOptions({
    queryKey: [
      ...topicsPrefix(slug),
      filter.kinds?.join(",") ?? "all",
      filter.spaceId ?? "",
      filter.q ?? "",
    ] as const,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ limit: String(TOPIC_PAGE_MAX) });
      if (filter.kinds?.length) q.set("kinds", filter.kinds.join(","));
      if (filter.spaceId) q.set("spaceId", filter.spaceId);
      if (filter.q) q.set("q", filter.q);
      if (pageParam) q.set("cursor", pageParam);
      return api<TopicPage>(`${workspaceConvPlace(slug).base}/topics?${q}`);
    },
    getNextPageParam: (last) => last.cursor,
    placeholderData: keepPreviousData,
  });

export const topicPrefix = (place: ConvPlace) =>
  [...place.key, "topic"] as const;
export const topicKey = (place: ConvPlace, idOrKey: string) =>
  [...topicPrefix(place), idOrKey] as const;

/** One topic by key or id (the API takes both). */
export const topicQuery = (api: Api, place: ConvPlace, idOrKey: string) =>
  queryOptions({
    queryKey: topicKey(place, idOrKey),
    queryFn: () => api<TopicView>(`${place.base}/topics/${enc(idOrKey)}`),
  });

/** What a topic is called on the screen. */
export const topicLabel = (t: TopicView) => t.name ?? t.label;

// ---- one topic read again after an event ---------------------------------------
//
// A message, a read or a change of a topic never reads the topics' pages again
// (all of them, one after another) nor the other topics: the topic alone is
// read and put where it shows, its row in the loaded pages moved to where its
// last activity puts it. Events of a topic arriving together read it once.

/** What a list of topics takes, read from its key (kinds, space, word). */
interface TopicListFilter {
  kinds: readonly string[] | null;
  spaceId: string | null;
  q: string;
}

function topicListFilterOf(
  key: QueryKey,
  slug: string,
): TopicListFilter | null {
  const [kinds, spaceId, q] = key.slice(topicsPrefix(slug).length);
  if (typeof kinds !== "string") return null;
  return {
    kinds: kinds === "all" ? null : kinds.split(","),
    spaceId: typeof spaceId === "string" && spaceId !== "" ? spaceId : null,
    q: typeof q === "string" ? q.trim().toLowerCase() : "",
  };
}

const activityOf = (t: TopicView) => t.lastMessageAt ?? t.createdAt;

/** The server's order: the latest activity first, then the id. */
const topicBefore = (a: TopicView, b: TopicView) =>
  activityOf(a) > activityOf(b) ||
  (activityOf(a) === activityOf(b) && a.id > b.id);

/**
 * The pages with the topic as read now (null: gone). A topic new to the pages
 * comes in when the filter takes it (a typed word only by its label; the
 * server also looks in the first message) and it falls within what was read.
 */
function placeTopicInPages(
  pages: readonly TopicPage[],
  topicId: string,
  topic: TopicView | null,
  f: TopicListFilter,
): readonly TopicPage[] {
  const was = pages.some((p) => p.topics.some((t) => t.id === topicId));
  const keep =
    topic !== null &&
    topic.place === "space" &&
    (f.kinds === null || f.kinds.includes(topic.kind)) &&
    (f.spaceId === null || topic.spaceId === f.spaceId) &&
    (was || !f.q || topicLabel(topic).toLowerCase().includes(f.q));
  if (!was && !keep) return pages;
  const out = pages.map((p) => ({
    ...p,
    topics: p.topics.filter((t) => t.id !== topicId),
  }));
  if (!keep || !topic || out.length === 0) return out;
  for (const [i, p] of out.entries()) {
    const at = p.topics.findIndex((t) => topicBefore(topic, t));
    if (at < 0) continue;
    out[i] = {
      ...p,
      topics: [...p.topics.slice(0, at), topic, ...p.topics.slice(at)],
    };
    return out;
  }
  const last = out.length - 1;
  if (was || out[last].cursor === null)
    out[last] = { ...out[last], topics: [...out[last].topics, topic] };
  return out;
}

/**
 * Puts a topic as read now (null: gone or not mine to see) where it shows:
 * under its id, key and DM peer, and its row in the loaded topic pages.
 */
export function placeTopic(
  qc: QueryClient,
  place: ConvPlace,
  topicId: string,
  topic: TopicView | null,
) {
  if (topic) putTopic(qc, place, topic);
  else
    for (const prefix of [topicPrefix(place), dmPrefix(place)])
      void qc.invalidateQueries({
        queryKey: prefix,
        predicate: (q) =>
          (q.state.data as TopicView | null | undefined)?.id === topicId,
      });
  if (!place.slug) return;
  for (const [key, data] of qc.getQueriesData<InfiniteData<TopicPage>>({
    queryKey: topicsPrefix(place.slug),
  })) {
    const filter = topicListFilterOf(key, place.slug);
    if (!data || !filter) continue;
    const pages = placeTopicInPages(data.pages, topicId, topic, filter);
    if (pages !== data.pages) qc.setQueryData(key, { ...data, pages });
  }
}

/** How long events of a topic are gathered before it is read. */
const TOPIC_READ_WAIT_MS = 30;

interface TopicReading {
  fetching: boolean;
  again: boolean;
  done: Promise<void>;
}

/** The topics being read, per app (a query client), never shared. */
const topicReadings = new WeakMap<QueryClient, Map<string, TopicReading>>();

/** The others of a direct message the screen holds (personal ones are read by them). */
function peersOf(qc: QueryClient, place: ConvPlace, topicId: string) {
  const held = [
    ...qc
      .getQueriesData<TopicView | null>({ queryKey: dmPrefix(place) })
      .map(([, t]) => t),
    ...qc
      .getQueriesData<TopicView>({ queryKey: topicPrefix(place) })
      .map(([, t]) => t),
    ...(qc.getQueryData<DmListView[]>(dmsKey(place)) ?? []),
  ];
  const topic = held.find((t) => t?.id === topicId);
  return topic ? dmPeersOf(topic) : null;
}

async function readTopicOnce(
  qc: QueryClient,
  api: Api,
  place: ConvPlace,
  topicId: string,
) {
  let topic: TopicView | null;
  try {
    if (place.slug)
      topic = await api<TopicView>(`${place.base}/topics/${enc(topicId)}`);
    else {
      // A personal direct message is read by its other person.
      const peers = peersOf(qc, place, topicId);
      if (!peers) {
        // A first message: the event does not name its people, and the open
        // conversation with them holds no topic yet. The list and the
        // conversations without a topic (one request each, no pages).
        void qc.invalidateQueries({ queryKey: dmsKey(place) });
        void qc.invalidateQueries({
          queryKey: dmPrefix(place),
          predicate: (q) => q.state.data === null,
        });
        return;
      }
      topic = (await api<DmView>(`${place.dms}/${enc(dmPeersPath(peers))}`))
        .topic;
    }
  } catch (error) {
    if (
      !(
        error instanceof ApiError &&
        (error.status === 404 || error.status === 403)
      )
    ) {
      // Not read: the pages and the topic are read when next shown.
      if (place.slug)
        void qc.invalidateQueries({
          queryKey: topicsPrefix(place.slug),
          refetchType: "none",
        });
      void qc.invalidateQueries({ queryKey: topicPrefix(place) });
      return;
    }
    topic = null;
  }
  placeTopic(qc, place, topicId, topic);
  // The direct messages' list shows the last message (one request, no pages).
  if (topic?.place !== "space")
    void qc.invalidateQueries({ queryKey: dmsKey(place) });
  // A space's overview shows its topics.
  if (place.slug)
    void qc.invalidateQueries({
      queryKey: topic?.spaceId
        ? overviewKey(place.slug, topic.spaceId)
        : overviewPrefix(place.slug),
    });
}

/**
 * Reads one topic again after an event and puts it where it shows. Events of
 * the topic within TOPIC_READ_WAIT_MS make one read; events during a read
 * make one more after it.
 */
export function refreshTopic(
  qc: QueryClient,
  api: Api,
  place: ConvPlace,
  topicId: string,
): Promise<void> {
  let map = topicReadings.get(qc);
  if (!map) {
    map = new Map();
    topicReadings.set(qc, map);
  }
  const id = `${place.key.join("/")}:${topicId}`;
  const going = map.get(id);
  if (going) {
    if (going.fetching) going.again = true;
    return going.done;
  }
  const all = map;
  const reading: TopicReading = {
    fetching: false,
    again: false,
    done: Promise.resolve(),
  };
  all.set(id, reading);
  reading.done = (async () => {
    try {
      await new Promise((r) => setTimeout(r, TOPIC_READ_WAIT_MS));
      do {
        reading.again = false;
        reading.fetching = true;
        await readTopicOnce(qc, api, place, topicId);
        reading.fetching = false;
      } while (reading.again);
    } finally {
      all.delete(id);
    }
  })();
  return reading.done;
}

// ---- messages ------------------------------------------------------------------

export interface MessagesData {
  /** seq order, newest last, without gaps. */
  messages: MessageView[];
  /** Whether there are older ones before the first. */
  more: boolean;
  /**
   * Messages not read between the head and the rest, after a jump to the
   * head (a tap on the status bar): read as the person comes down to them.
   */
  gap?: ColumnGap | null;
}

export const messagesKey = (place: ConvPlace, topicId: string) =>
  [...place.key, "messages", topicId] as const;

const messagesPath = (place: ConvPlace, topicId: string) =>
  `${place.base}/topics/${enc(topicId)}/messages`;

export const messagesQuery = (api: Api, place: ConvPlace, topicId: string) =>
  queryOptions({
    queryKey: messagesKey(place, topicId),
    queryFn: async ({ client }): Promise<MessagesData> => {
      const old = client.getQueryData<MessagesData>(
        messagesKey(place, topicId),
      );
      const first = old?.messages[0];
      if (!old || !first) {
        const page = await api<MessagePage>(
          `${messagesPath(place, topicId)}?limit=${MESSAGES_PAGE}`,
        );
        return { messages: page.messages, more: page.more };
      }
      // The stretch already shown, read again, and what came after it. With
      // a gap, the stretch above it and the one below (not the gap itself).
      const stretch = async (from: number, until: number | null) => {
        const out: MessageView[] = [];
        let after = from - 1;
        for (;;) {
          const page = await api<MessagePage>(
            `${messagesPath(place, topicId)}?afterSeq=${after}&limit=${MESSAGE_PAGE_MAX}`,
          );
          out.push(
            ...page.messages.filter((x) => until === null || x.seq <= until),
          );
          const last = page.messages.at(-1);
          if (
            !last ||
            page.messages.length < MESSAGE_PAGE_MAX ||
            (until !== null && last.seq >= until)
          )
            break;
          after = last.seq;
        }
        return out;
      };
      const gap = old.gap ?? null;
      if (!gap)
        return { messages: await stretch(first.seq, null), more: old.more };
      const [above, below] = await Promise.all([
        stretch(first.seq, gap.after),
        stretch(gap.before, null),
      ]);
      return { messages: [...above, ...below], more: old.more, gap };
    },
  });

/**
 * The head of the topic, without the pages between it and what is loaded (a
 * tap on the status bar): those are read later as the person comes down to
 * them (`readGapMessages`).
 */
export async function fetchHeadMessages(
  api: Api,
  qc: QueryClient,
  place: ConvPlace,
  topicId: string,
): Promise<void> {
  const key = messagesKey(place, topicId);
  const old = qc.getQueryData<MessagesData>(key);
  if (!old?.messages[0] || !old.more || old.gap) return;
  const page = await api<MessagePage>(
    `${messagesPath(place, topicId)}?afterSeq=0&limit=${MESSAGES_PAGE}`,
  );
  qc.setQueryData<MessagesData>(key, (cur) => {
    if (!cur?.more || cur.gap) return cur;
    const put = putJumpPage(cur.messages, page.messages);
    return put
      ? { messages: put.items, more: put.hasOlder, gap: put.gap }
      : cur;
  });
}

/**
 * Reads the next page of the gap from its top and answers how to put it in
 * (null: no gap).
 */
export async function readGapMessages(
  api: Api,
  qc: QueryClient,
  place: ConvPlace,
  topicId: string,
): Promise<(() => void) | null> {
  const key = messagesKey(place, topicId);
  const gap = qc.getQueryData<MessagesData>(key)?.gap;
  if (!gap) return null;
  const page = await api<MessagePage>(
    `${messagesPath(place, topicId)}?afterSeq=${gap.after}&limit=${MESSAGES_PAGE}`,
  );
  return () =>
    qc.setQueryData<MessagesData>(key, (cur) => {
      if (!cur?.gap || cur.gap.after !== gap.after) return cur;
      const put = putGapPage(cur.messages, gap, page.messages, MESSAGES_PAGE);
      return { ...cur, messages: put.items, gap: put.gap };
    });
}

/** Reads the page before the first message shown and puts it in front. */
export async function fetchOlderMessages(
  api: Api,
  qc: QueryClient,
  place: ConvPlace,
  topicId: string,
): Promise<void> {
  const key = messagesKey(place, topicId);
  const old = qc.getQueryData<MessagesData>(key);
  const first = old?.messages[0];
  if (!old || !first || !old.more) return;
  const page = await api<MessagePage>(
    `${messagesPath(place, topicId)}?beforeSeq=${first.seq}&limit=${MESSAGES_PAGE}`,
  );
  qc.setQueryData<MessagesData>(key, (cur) => {
    if (!cur || cur.messages[0]?.seq !== first.seq) return cur;
    return {
      ...cur,
      messages: [...page.messages, ...cur.messages],
      more: page.more,
    };
  });
}

/** Where a post goes: an existing topic, or a direct message to these others. */
export type PostTarget =
  | { topicId: string }
  | { peers: readonly string[]; topicId: string | null };

/** A post target's name (a direct message's: `dm:` and its path, the same as one person's id). */
export const postTargetId = (t: PostTarget) =>
  "peers" in t ? `dm:${dmPeersPath(t.peers)}` : t.topicId;

const postKey = (place: ConvPlace, target: PostTarget) =>
  [...place.key, "post", postTargetId(target)] as const;

export interface PostInput {
  /** The pending row's identity until the server answers. */
  clientId: string;
  body: string;
  fileIds: string[];
  files: MessageView["files"];
  createdAt: string;
}

/** Puts a stored message into the topic's stretch when it closes the stretch. */
function placeMessage(qc: QueryClient, place: ConvPlace, msg: MessageView) {
  qc.setQueryData<MessagesData>(messagesKey(place, msg.topicId), (cur) => {
    if (!cur) return { messages: [msg], more: msg.seq > 1 };
    const rest = cur.messages.filter((x) => x.seq !== msg.seq);
    const last = rest.at(-1);
    if (last && last.seq !== msg.seq - 1) return cur;
    return { ...cur, messages: [...rest, msg] };
  });
}

/** Writes a topic as the server answered it under its id, key and DM peers. */
export function putTopic(qc: QueryClient, place: ConvPlace, topic: TopicView) {
  qc.setQueryData(topicKey(place, topic.id), topic);
  qc.setQueryData(topicKey(place, topic.key), topic);
  const peers = dmPeersOf(topic);
  if (peers) qc.setQueryData(dmKey(place, peers), topic);
}

export function usePostMessage(place: ConvPlace, target: PostTarget) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: postKey(place, target),
    mutationFn: (input: PostInput) => {
      const payload = { body: input.body, fileIds: input.fileIds };
      const idem = idempotencyKeyFor({ clientId: input.clientId });
      if ("peers" in target)
        return api<PostResult>(
          `${place.dms}/${enc(dmPeersPath(target.peers))}/messages`,
          { method: "POST", body: payload, idempotencyKey: idem },
        );
      return api<PostResult>(`${place.base}/messages`, {
        method: "POST",
        body: { topicId: target.topicId, ...payload },
        idempotencyKey: idem,
      });
    },
    onSuccess: (result) => {
      placeMessage(qc, place, result.message);
      placeTopic(qc, place, result.topic.id, result.topic);
    },
    onSettled: (result) =>
      Promise.all([
        result &&
          qc.invalidateQueries({
            queryKey: messagesKey(place, result.topic.id),
          }),
        settleLists(qc, place),
      ]),
  });
}

/** The posts of a topic still on their way, oldest first. */
export function usePendingPosts(
  place: ConvPlace,
  target: PostTarget,
): PostInput[] {
  return useMutationState({
    filters: { mutationKey: [...postKey(place, target)], status: "pending" },
    select: (mutation) => mutation.state.variables as PostInput,
  });
}

/** The latest failed post of a topic (its text goes back into the box). */
export function useFailedPost(
  place: ConvPlace,
  target: PostTarget,
): { error: unknown; input: PostInput } | null {
  const states = useMutationState({
    filters: { mutationKey: [...postKey(place, target)] },
    select: (mutation) => mutation.state,
  });
  const last = states.at(-1);
  if (last?.status !== "error") return null;
  return { error: last.error, input: last.variables as PostInput };
}

export const newClientId = newOptimisticId;

export function useEditMessage(place: ConvPlace, topicId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: string; body: string }) =>
      api<MessageView>(`${place.base}/messages/${enc(input.messageId)}`, {
        method: "PATCH",
        body: { body: input.body },
      }),
    ...optimisticMutation<MessagesData, { messageId: string; body: string }>(
      qc,
      messagesKey(place, topicId),
      (old, { messageId, body }) =>
        old && {
          ...old,
          messages: old.messages.map((x) =>
            x.id === messageId
              ? { ...x, body, editedAt: new Date().toISOString() }
              : x,
          ),
        },
    ),
  });
}

export function useRemoveMessage(place: ConvPlace, topicId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: string }) =>
      api(`${place.base}/messages/${enc(input.messageId)}`, {
        method: "DELETE",
      }),
    ...optimisticMutation<MessagesData, { messageId: string }>(
      qc,
      messagesKey(place, topicId),
      (old, { messageId }) =>
        old && {
          ...old,
          messages: old.messages.map((x) =>
            x.id === messageId
              ? {
                  ...x,
                  body: "",
                  files: [],
                  reactions: [],
                  removedAt: new Date().toISOString(),
                }
              : x,
          ),
        },
    ),
  });
}

/** Adds or takes back my reaction (the count moves at once). */
export function useToggleReaction(place: ConvPlace, topicId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: string; kind: string; on: boolean }) =>
      api(
        `${place.base}/messages/${enc(input.messageId)}/reactions/${enc(input.kind)}`,
        { method: input.on ? "PUT" : "DELETE" },
      ),
    ...optimisticMutation<
      MessagesData,
      { messageId: string; kind: string; on: boolean }
    >(qc, messagesKey(place, topicId), (old, { messageId, kind, on }) =>
      old
        ? {
            ...old,
            messages: old.messages.map((x) =>
              x.id === messageId
                ? { ...x, reactions: toggled(x.reactions, kind, on) }
                : x,
            ),
          }
        : old,
    ),
  });
}

/** Reactions with mine added or taken back, the most used first. */
export function toggled(
  reactions: readonly ReactionView[],
  kind: string,
  on: boolean,
): ReactionView[] {
  const found = reactions.find((r) => r.kind === kind);
  let next: ReactionView[];
  if (on) {
    if (found?.mine) return [...reactions];
    next = found
      ? reactions.map((r) =>
          r.kind === kind ? { ...r, count: r.count + 1, mine: true } : r,
        )
      : [...reactions, { kind, count: 1, mine: true }];
  } else {
    if (!found?.mine) return [...reactions];
    next = reactions
      .map((r) =>
        r.kind === kind ? { ...r, count: r.count - 1, mine: false } : r,
      )
      .filter((r) => r.count > 0);
  }
  return next.sort((a, b) => b.count - a.count);
}

// ---- reading -------------------------------------------------------------------

const withRead = <T extends TopicView>(t: T, seq: number): T =>
  seq <= t.readSeq ? t : { ...t, readSeq: seq, unread: 0 };

/** Moves my read point of a topic forward (never back). */
export function useMarkRead(place: ConvPlace) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { topic: TopicView; seq: number }) =>
      api(`${place.base}/topics/${enc(input.topic.id)}/read`, {
        method: "POST",
        body: { seq: input.seq },
      }),
    onMutate: async ({ topic, seq }) => {
      for (const k of [topic.id, topic.key])
        qc.setQueryData<TopicView>(topicKey(place, k), (t) =>
          t ? withRead(t, seq) : t,
        );
      const peers = dmPeersOf(topic);
      if (peers)
        qc.setQueryData<TopicView | null>(dmKey(place, peers), (t) =>
          t ? withRead(t, seq) : t,
        );
      if (place.slug)
        qc.setQueriesData<{ pages: TopicPage[]; pageParams: unknown[] }>(
          { queryKey: topicsPrefix(place.slug) },
          (data) =>
            data && {
              ...data,
              pages: data.pages.map((p) => ({
                ...p,
                topics: p.topics.map((t) =>
                  t.id === topic.id ? withRead(t, seq) : t,
                ),
              })),
            },
        );
      qc.setQueryData<DmListView[]>(dmsKey(place), (list) =>
        list?.map((t) => (t.id === topic.id ? withRead(t, seq) : t)),
      );
      if (place.slug)
        qc.setQueryData<InboxItem[]>(inboxKey(place.slug), (items) =>
          items?.filter((i) => i.topic.id !== topic.id),
        );
      // Me's Inbox (its rows of direct messages and mentions) too.
      await inboxRowsSettled(qc, [`dm:${topic.id}`, `mention:${topic.id}`]);
    },
    onSettled: () => settleLists(qc, place),
  });
}

/**
 * The lists that change with a post or a read: direct messages, inbox,
 * unreads. Not the topics' pages (all read again each time): the row is put
 * in place by the writer and by the topic's event (`refreshTopic`). Read
 * again while not shown too: the person comes back to them from the
 * conversation they opened, and they must be right by then.
 */
export function settleLists(qc: QueryClient, place: ConvPlace) {
  const keys: QueryKey[] = [dmsKey(place)];
  if (place.slug) keys.push(inboxKey(place.slug), unreadsKey(place.slug));
  return Promise.all(
    keys.map((queryKey) =>
      qc.invalidateQueries({ queryKey, refetchType: "all" }),
    ),
  );
}

export const unreadsKey = (slug: string) =>
  ["ws", slug, "cv", "unreads"] as const;

export const unreadsQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: unreadsKey(slug),
    queryFn: () =>
      api<UnreadSummary>(`${workspaceConvPlace(slug).base}/unreads`),
  });

// ---- new topics ----------------------------------------------------------------

export interface NewTopicInput {
  spaceId: string;
  name: string | null;
  body: string;
  fileIds: string[];
  kind: TopicKind;
}

export function useCreateTopic(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  const place = workspaceConvPlace(slug);
  return useMutation({
    mutationFn: (input: NewTopicInput) =>
      api<PostResult>(`${place.base}/topics`, {
        method: "POST",
        body: {
          spaceId: input.spaceId,
          ...(input.name ? { name: input.name } : {}),
          body: input.body,
          fileIds: input.fileIds,
          kind: input.kind,
        },
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSuccess: (result) => {
      placeTopic(qc, place, result.topic.id, result.topic);
      qc.setQueryData<MessagesData>(messagesKey(place, result.topic.id), {
        messages: [result.message],
        more: false,
      });
    },
    onSettled: () => settleLists(qc, place),
  });
}

/** Renames a topic or moves it to another space. */
export function useUpdateTopic(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  const place = workspaceConvPlace(slug);
  return useMutation({
    mutationFn: (input: {
      topic: TopicView;
      patch: { name?: string | null; spaceId?: string };
    }) =>
      api<TopicView>(`${place.base}/topics/${enc(input.topic.id)}`, {
        method: "PATCH",
        body: input.patch,
      }),
    onSuccess: (topic) => placeTopic(qc, place, topic.id, topic),
    onSettled: () => settleLists(qc, place),
  });
}

// ---- direct messages -----------------------------------------------------------

export const dmsKey = (place: ConvPlace) => [...place.key, "dm-list"] as const;

/** My direct messages, the latest first. */
export const dmsQuery = (api: Api, place: ConvPlace) =>
  queryOptions({
    queryKey: dmsKey(place),
    queryFn: async () =>
      (await api<{ topics: DmListView[] }>(place.dms)).topics,
  });

/** The others of a direct message (their ids, as its path names them); null for a space's topic. */
export const dmPeersOf = (topic: TopicView): string[] | null =>
  topic.place === "dm" && topic.peers.length > 0
    ? topic.peers.map((p) => p.userId)
    : null;

/** A direct message's others by name, joined (what one without a name is called; gone accounts left out). */
export const dmNames = (peers: readonly PersonRef[]): string =>
  peers
    .map((p) => p.name)
    .filter((n) => n !== "")
    .join(", ");

export const dmPrefix = (place: ConvPlace) => [...place.key, "dm"] as const;
/** The direct message with these others, in whatever order they are named. */
export const dmKey = (place: ConvPlace, peers: readonly string[]) =>
  [...dmPrefix(place), dmPeersPath(peers)] as const;

/** The direct message with these others; null when none was written yet. */
export const dmQuery = (api: Api, place: ConvPlace, peers: readonly string[]) =>
  queryOptions({
    queryKey: dmKey(place, peers),
    queryFn: async (): Promise<TopicView | null> => {
      try {
        return (await api<DmView>(`${place.dms}/${enc(dmPeersPath(peers))}`))
          .topic;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });

/** People one can start a personal direct message with (those one shares a workspace with). */
export const teamPeopleQuery = (api: Api, q: string) =>
  queryOptions({
    queryKey: [...PERSONAL_DMS.key, "people", q] as const,
    queryFn: async () =>
      (
        await api<{ users: TeamUserView[] }>(
          `/api/team/users?q=${enc(q.trim())}`,
        )
      ).users,
    staleTime: 30_000,
  });

// ---- the inbox -----------------------------------------------------------------

export const inboxKey = (slug: string) => ["ws", slug, "cv", "inbox"] as const;

export const inboxQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: inboxKey(slug),
    queryFn: async () =>
      (
        await api<{ items: InboxItem[] }>(
          `${workspaceConvPlace(slug).base}/inbox`,
        )
      ).items,
  });

// ---- mentions ------------------------------------------------------------------

/** Members whose name has the query, with whether each sees the space. */
export const mentionCandidatesQuery = (
  api: Api,
  slug: string,
  spaceId: string | null,
  q: string,
) =>
  queryOptions({
    queryKey: ["ws", slug, "cv", "mention", spaceId ?? "-", q] as const,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "20" });
      if (q.trim()) params.set("q", q.trim());
      if (spaceId) params.set("spaceId", spaceId);
      return (
        await api<{ members: MemberView[] }>(
          `/api/w/${enc(slug)}/members?${params}`,
        )
      ).members;
    },
    staleTime: 30_000,
  });

/** Topics a `[[` link may point to. */
export const linkCandidatesQuery = (api: Api, slug: string, q: string) =>
  queryOptions({
    queryKey: ["ws", slug, "cv", "link", q] as const,
    queryFn: async (): Promise<Array<{ id: string; label: string }>> => {
      if (q.trim() === "") {
        const page = await api<TopicPage>(
          `${workspaceConvPlace(slug).base}/topics?limit=10`,
        );
        return page.topics.map((t) => ({ id: t.id, label: topicLabel(t) }));
      }
      const res = await api<{
        results: Record<string, Array<{ id: string; label: string }>>;
      }>(`/api/search?types=topic&limit=10&q=${enc(q.trim())}`);
      return res.results.topic ?? [];
    },
    staleTime: 30_000,
  });

// ---- emoji ---------------------------------------------------------------------

export const emojisKey = (slug: string) =>
  ["ws", slug, "cv", "emojis"] as const;

/** The workspace's custom emoji, by name. */
export const emojisQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: emojisKey(slug),
    queryFn: async () =>
      (
        await api<{ emojis: EmojiView[] }>(
          `${workspaceConvPlace(slug).base}/emojis`,
        )
      ).emojis,
    staleTime: 5 * 60_000,
  });

export function useCreateEmoji(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; fileId: string }) =>
      api<EmojiView>(`${workspaceConvPlace(slug).base}/emojis`, {
        method: "POST",
        body: input,
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: emojisKey(slug) }),
  });
}

export function useRemoveEmoji(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { emojiId: string; name: string }) =>
      api(`${workspaceConvPlace(slug).base}/emojis/${enc(input.emojiId)}`, {
        method: "DELETE",
      }),
    ...optimisticMutation<EmojiView[], { emojiId: string }>(
      qc,
      emojisKey(slug),
      (old, { emojiId }) => old?.filter((e) => e.id !== emojiId),
    ),
  });
}

// ---- a space's overview and pins -----------------------------------------------

export const overviewPrefix = (slug: string) =>
  ["ws", slug, "cv", "overview"] as const;
export const overviewKey = (slug: string, spaceId: string) =>
  [...overviewPrefix(slug), spaceId] as const;

/**
 * The overviews of every workspace's spaces are read again (a pinned
 * document's name or place changed; its event does not say the workspace).
 */
export const invalidateOverviews = (qc: QueryClient) =>
  qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "ws" &&
      q.queryKey[2] === "cv" &&
      q.queryKey[3] === "overview",
  });

export const overviewQuery = (api: Api, slug: string, spaceId: string) =>
  queryOptions({
    queryKey: overviewKey(slug, spaceId),
    queryFn: () =>
      api<SpaceOverview>(
        `${workspaceConvPlace(slug).base}/spaces/${enc(spaceId)}/overview`,
      ),
  });

/** Pinning (`on`) or unpinning a topic or a document; what to show while it goes. */
export interface PinInput {
  kind: PinKind;
  targetId: string;
  on: boolean;
  topic?: TopicView;
  page?: PinnedDocument;
}

export function usePin(slug: string, spaceId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PinInput) =>
      api(
        `${workspaceConvPlace(slug).base}/spaces/${enc(spaceId)}/pins/${enc(input.kind)}/${enc(input.targetId)}`,
        { method: input.on ? "PUT" : "DELETE" },
      ),
    ...optimisticMutation<SpaceOverview, PinInput>(
      qc,
      overviewKey(slug, spaceId),
      (old, input) => {
        if (!old) return old;
        const rest = old.pins.filter(
          (p) => !(p.kind === input.kind && p.targetId === input.targetId),
        );
        if (!input.on) return { ...old, pins: rest };
        return {
          ...old,
          pins: [
            ...rest,
            {
              kind: input.kind,
              targetId: input.targetId,
              topic: input.topic ?? null,
              page: input.page ?? null,
              position: rest.length,
            },
          ],
        };
      },
    ),
  });
}

/** Puts the pins of a space in a new order. */
export function useReorderPins(slug: string, spaceId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { pins: Array<{ kind: PinKind; targetId: string }> }) =>
      api(
        `${workspaceConvPlace(slug).base}/spaces/${enc(spaceId)}/pins/order`,
        { method: "PUT", body: input },
      ),
    ...optimisticMutation<
      SpaceOverview,
      { pins: Array<{ kind: PinKind; targetId: string }> }
    >(qc, overviewKey(slug, spaceId), (old, { pins }) => {
      if (!old) return old;
      const at = (p: { kind: string; targetId: string }) =>
        pins.findIndex((x) => x.kind === p.kind && x.targetId === p.targetId);
      return {
        ...old,
        pins: [...old.pins]
          .sort((a, b) => at(a) - at(b))
          .map((p, position) => ({ ...p, position })),
      };
    }),
  });
}
